import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2, AlertTriangle, Lightbulb, Sparkles, Zap } from "lucide-react";

export default function AIReportCard({ result }) {
  if (!result) return null;
  const score = result.ai_score;
  const scoreColor = score >= 80 ? "#10B981" : score >= 50 ? "#FBBF24" : "#F87171";
  const circumference = 2 * Math.PI * 34;
  const offset = circumference - (score / 100) * circumference;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      className="mt-4 glass-panel rounded-2xl p-6 border border-indigo-400/20" data-testid="ai-report-card"
    >
      <div className="flex items-center gap-2 mb-5">
        <Sparkles className="w-4 h-4 text-indigo-400" />
        <h3 className="font-heading font-semibold text-white text-sm">AI Project Evaluator Report</h3>
      </div>

      <div className="flex items-center gap-6 mb-5">
        <div className="relative w-20 h-20 shrink-0">
          <svg className="w-20 h-20 -rotate-90">
            <circle cx="40" cy="40" r="34" stroke="rgba(255,255,255,0.08)" strokeWidth="6" fill="none" />
            <motion.circle
              cx="40" cy="40" r="34" stroke={scoreColor} strokeWidth="6" fill="none"
              strokeDasharray={circumference} strokeLinecap="round"
              initial={{ strokeDashoffset: circumference }}
              animate={{ strokeDashoffset: offset }}
              transition={{ duration: 1, ease: "easeOut" }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="font-heading text-xl font-bold text-white" data-testid="ai-report-score">{score}</span>
          </div>
        </div>
        <div className="flex-1">
          <p className="text-sm text-slate-300 leading-relaxed" data-testid="ai-report-summary">{result.ai_summary || result.ai_feedback}</p>
          {result.xp_bonus > 0 && (
            <span className="inline-flex items-center gap-1 mt-2 bg-[#FBBF24]/10 border border-[#FBBF24]/30 rounded-full px-2.5 py-0.5 text-[11px] font-bold text-[#FBBF24]">
              <Zap className="w-3 h-3" /> +{result.xp_bonus} Bonus XP
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {result.ai_correctness && (
          <div className="bg-white/[0.02] rounded-lg p-3" data-testid="ai-report-correctness">
            <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-400 flex items-center gap-1 mb-1.5"><CheckCircle2 className="w-3 h-3" /> Correctness</p>
            <p className="text-xs text-slate-400 leading-relaxed">{result.ai_correctness}</p>
          </div>
        )}
        {result.ai_best_practices && (
          <div className="bg-white/[0.02] rounded-lg p-3" data-testid="ai-report-best-practices">
            <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-400 flex items-center gap-1 mb-1.5"><Sparkles className="w-3 h-3" /> Best Practices</p>
            <p className="text-xs text-slate-400 leading-relaxed">{result.ai_best_practices}</p>
          </div>
        )}
        {result.ai_mistakes?.length > 0 && (
          <div className="bg-white/[0.02] rounded-lg p-3" data-testid="ai-report-mistakes">
            <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-400 flex items-center gap-1 mb-1.5"><AlertTriangle className="w-3 h-3" /> Mistakes Found</p>
            <ul className="space-y-1">
              {result.ai_mistakes.map((m, i) => <li key={i} className="text-xs text-slate-400">• {m}</li>)}
            </ul>
          </div>
        )}
        {result.ai_suggestions?.length > 0 && (
          <div className="bg-white/[0.02] rounded-lg p-3" data-testid="ai-report-suggestions">
            <p className="text-[10px] font-bold uppercase tracking-wider text-purple-400 flex items-center gap-1 mb-1.5"><Lightbulb className="w-3 h-3" /> Suggestions</p>
            <ul className="space-y-1">
              {result.ai_suggestions.map((s, i) => <li key={i} className="text-xs text-slate-400">• {s}</li>)}
            </ul>
          </div>
        )}
      </div>
    </motion.div>
  );
}
